import {useEffect, useMemo, useState} from "react";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {getCaptcha, login, passwordReset, register, validateInviteCode, ApiError, type FieldRule} from "@corporatesclub/shared";
import {useFormValidation} from "@/lib/useFormValidation";
import {useI18n} from "@/components/shop/I18nProvider";
import {useSession} from "@/components/shop/SessionProvider";
import {useShopSite} from "@/components/shop/ShopSiteProvider";
import {AccountModal} from "@/components/shop/AccountModal";
import {writeSessionCache} from "@/lib/shopCache";

type LoginField = "email" | "parola";
type RegField = "nume" | "lnume" | "telefon" | "email" | "parola" | "cod";
type ResetField = "email" | "cod";

const loginRules: FieldRule<LoginField>[] = [
  {field: "email", type: "email", required: true},
  {field: "parola", type: "password", required: true}
];
const regRulesBase: FieldRule<RegField>[] = [
  {field: "nume", required: true},
  {field: "lnume", required: true},
  {field: "telefon", required: true},
  {field: "email", type: "email", required: true},
  {field: "parola", type: "password", required: true}
];
const regRulesWithCode: FieldRule<RegField>[] = [
  ...regRulesBase,
  {field: "cod", required: true}
];
const resetRules: FieldRule<ResetField>[] = [
  {field: "email", type: "email", required: true},
  {field: "cod", required: true}
];

function Field({label, children, error}: {label: string; children: React.ReactNode; error?: string}) {
  return (
    <label className={`cc-auth-field${error ? " cc-field--invalid" : ""}`}>
      <span className="cc-auth-label">{label}</span>
      {children}
      {error ? <span className="cc-field__error" role="alert">{error}</span> : null}
    </label>
  );
}

export function AutentificarePage() {
  const {t} = useI18n();
  const navigate = useNavigate();
  const {user, ready} = useSession();
  const {status, ready: siteReady} = useShopSite();
  const [params] = useSearchParams();
  const code = params.get("code") || "";
  const publicAccess = status.publicAccess;
  const shellReady = ready && siteReady;
  const embedded = shellReady && publicAccess;
  const splitLayout = embedded && !code;
  const [tab, setTab] = useState<"login" | "register">("login");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [firmaId, setFirmaId] = useState<number | "">("");
  const [firmaNume, setFirmaNume] = useState("");
  const [loginForm, setLoginForm] = useState({email: "", parola: ""});
  const [regForm, setRegForm] = useState({nume: "", lnume: "", email: "", telefon: "", parola: "", cod: code});
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetCod, setResetCod] = useState("");
  const [captcha, setCaptcha] = useState({nr1: 0, nr2: 0});
  const [resetMsg, setResetMsg] = useState("");
  const [resetError, setResetError] = useState("");
  const {errors: loginErrors, clearError: clearLoginError, validate: validateLogin} = useFormValidation<LoginField>();
  const {errors: regErrors, clearError: clearRegError, validate: validateReg} = useFormValidation<RegField>();
  const {errors: resetErrors, clearError: clearResetError, validate: validateReset} = useFormValidation<ResetField>();

  const activeRegRules = useMemo(() => {
    if(code || (publicAccess && !code)) return regRulesBase;
    return regRulesWithCode;
  }, [code, publicAccess]);

  useEffect(() => {
    if(ready && user?.authenticated) navigate("/", {replace: true});
  }, [ready, user?.authenticated, navigate]);

  useEffect(() => {
    if(code) return;
    getCaptcha().then(setCaptcha).catch(() => {});
  }, [code]);

  useEffect(() => {
    if(!code) return;
    setTab("register");
    validateInviteCode(code).then((r) => {
      if(r.valid && r.firmaId) {
        setFirmaId(r.firmaId);
        setFirmaNume(r.firmaNume || "");
        setRegForm((f) => ({...f, cod: code}));
      } else setError(t("The code used is not valid!"));
    }).catch(() => setError(t("The code used is not valid!")));
  }, [code, t]);

  function openResetModal() {
    setResetError("");
    setResetMsg("");
    setResetOpen(true);
    getCaptcha().then(setCaptcha).catch(() => {});
  }

  function closeResetModal() {
    setResetOpen(false);
    setResetError("");
  }

  async function onReset(e: React.FormEvent) {
    e.preventDefault();
    setResetError("");
    setError("");
    if(!validateReset(resetRules, {email: resetEmail, cod: resetCod}, t)) return;
    setLoading(true);
    setResetMsg("");
    try {
      await passwordReset({email: resetEmail, cod: parseInt(resetCod, 10)});
      setResetMsg(t("The new password has been sent by e-mail!"));
      setResetEmail("");
      setResetCod("");
      getCaptcha().then(setCaptcha).catch(() => {});
    } catch(err) {
      if(err instanceof ApiError && err.status === 404) setResetError(t("Wrong email address!"));
      else if(err instanceof ApiError && err.status === 400) setResetError(t("Incorrect captcha"));
      else setResetError(err instanceof Error ? err.message : t("Send failed"));
    } finally {
      setLoading(false);
    }
  }

  async function onLogin(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if(!validateLogin(loginRules, loginForm, t)) return;
    setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.parola);
      writeSessionCache(u);
      window.location.href = "/";
    } catch(err) {
      setError(err instanceof Error ? err.message : t("Login failed"));
    } finally {
      setLoading(false);
    }
  }

  async function onRegister(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if(!validateReg(activeRegRules, regForm, t)) return;
    setLoading(true);
    try {
      let body: Parameters<typeof register>[0];
      if(code && firmaId) {
        body = {
          nume: regForm.nume,
          lnume: regForm.lnume,
          email: regForm.email,
          telefon: regForm.telefon,
          parola: regForm.parola,
          firma: Number(firmaId)
        };
      } else if(publicAccess && !code) {
        body = {
          nume: regForm.nume,
          lnume: regForm.lnume,
          email: regForm.email,
          telefon: regForm.telefon,
          parola: regForm.parola
        };
      } else {
        body = {...regForm};
      }
      const u = await register(body);
      writeSessionCache(u);
      window.location.href = "/";
    } catch(err) {
      setError(err instanceof Error ? err.message : t("Registration failed"));
    } finally {
      setLoading(false);
    }
  }

  const loginFields = (
    <form className="cc-auth-form" id="contAuth" onSubmit={onLogin} noValidate>
      <Field label={t("Your e-mail address")} error={loginErrors.email}>
        <input type="email" name="email" className="cc-auth-input" value={loginForm.email} onChange={(e) => { setLoginForm({...loginForm, email: e.target.value}); clearLoginError("email"); }} aria-required="true" disabled={loading} autoComplete="username" />
      </Field>
      <Field label={t("Password")} error={loginErrors.parola}>
        <input type="password" name="parola" className="cc-auth-input" value={loginForm.parola} onChange={(e) => { setLoginForm({...loginForm, parola: e.target.value}); clearLoginError("parola"); }} aria-required="true" disabled={loading} autoComplete="current-password" />
      </Field>
      <button type="submit" className="cc-auth-submit" disabled={loading}>{loading ? "..." : t("Log in")}</button>
      <button type="button" className="cc-auth-link" onClick={openResetModal}>{t("Recover password")}</button>
    </form>
  );

  const registerFields = (showInviteCode: boolean) => (
    <form className="cc-auth-form" id="contNew" onSubmit={onRegister} autoComplete="off" noValidate>
      {showInviteCode ? (
        <Field label={t("Invite code")} error={regErrors.cod}>
          <input type="text" name="cod" className="cc-auth-input" value={regForm.cod} onChange={(e) => { setRegForm({...regForm, cod: e.target.value}); clearRegError("cod"); }} aria-required="true" disabled={loading} />
        </Field>
      ) : null}
      <div className="cc-auth-row">
        <Field label={t("First name")} error={regErrors.nume}>
          <input type="text" name="nume" className="cc-auth-input" value={regForm.nume} onChange={(e) => { setRegForm({...regForm, nume: e.target.value}); clearRegError("nume"); }} aria-required="true" disabled={loading} />
        </Field>
        <Field label={t("Last name")} error={regErrors.lnume}>
          <input type="text" name="lnume" className="cc-auth-input" value={regForm.lnume} onChange={(e) => { setRegForm({...regForm, lnume: e.target.value}); clearRegError("lnume"); }} aria-required="true" disabled={loading} />
        </Field>
      </div>
      <div className="cc-auth-row">
        <Field label={t("Phone")} error={regErrors.telefon}>
          <input type="text" name="telefon" className="cc-auth-input" value={regForm.telefon} onChange={(e) => { setRegForm({...regForm, telefon: e.target.value}); clearRegError("telefon"); }} aria-required="true" disabled={loading} />
        </Field>
        <Field label={t("Your e-mail address")} error={regErrors.email}>
          <input type="email" name="email" className="cc-auth-input" value={regForm.email} onChange={(e) => { setRegForm({...regForm, email: e.target.value}); clearRegError("email"); }} aria-required="true" disabled={loading} autoComplete="email" />
        </Field>
      </div>
      <Field label={t("Password")} error={regErrors.parola}>
        <input type="password" name="parola" className="cc-auth-input" value={regForm.parola} onChange={(e) => { setRegForm({...regForm, parola: e.target.value}); clearRegError("parola"); }} aria-required="true" disabled={loading} autoComplete="new-password" />
      </Field>
      <button type="submit" className="cc-auth-submit" disabled={loading}>{loading ? "..." : t("Create account")}</button>
    </form>
  );

  const resetModal = (
    <AccountModal open={resetOpen} title={t("Recover password")} onClose={closeResetModal}>
      <form className="cc-auth-form" id="contReset" onSubmit={onReset} noValidate>
        <p className="cc-auth-sub cc-auth-sub--modal">{t("Enter your email and captcha to receive a new password.")}</p>
        <Field label={t("Your e-mail address")} error={resetErrors.email}>
          <input type="email" name="email" className="cc-auth-input" value={resetEmail} onChange={(e) => { setResetEmail(e.target.value); clearResetError("email"); }} aria-required="true" disabled={loading} />
        </Field>
        <div className={`cc-auth-captcha${resetErrors.cod ? " cc-field--invalid" : ""}`}>
          <span>{t("The sum of")}</span>
          <b>{captcha.nr1} + {captcha.nr2}</b>
          <input type="text" name="cod" placeholder={t("The result is...")} className="cc-auth-input" style={{flex: 1, minWidth: 120}} value={resetCod} onChange={(e) => { setResetCod(e.target.value); clearResetError("cod"); }} aria-required="true" disabled={loading} />
          {resetErrors.cod ? <span className="cc-field__error" role="alert">{resetErrors.cod}</span> : null}
        </div>
        {resetMsg ? <p className="cc-auth-ok">{resetMsg}</p> : null}
        {resetError ? <p className="cc-auth-err">{resetError}</p> : null}
        <button type="submit" className="cc-auth-submit" disabled={loading}>{loading ? "..." : t("Reset password")}</button>
      </form>
    </AccountModal>
  );

  const messages = (
    <>
      {error ? <p className="cc-auth-err">{error}</p> : null}
    </>
  );

  if(!shellReady) {
    return <div className="cc-auth-page cc-auth-page--pending" aria-busy="true" />;
  }

  if(splitLayout) {
    return (
      <div className="cc-auth-page cc-auth-page--embedded">
        <div className="cc-auth-shell">
          <div className="cc-auth-split">
            <section className="cc-auth-panel">
              <p className="cc-auth-eyebrow">{t("Welcome back")}</p>
              <h1 className="cc-auth-title">{t("Log in")}</h1>
              <p className="cc-auth-sub">{t("Sign in to continue shopping")}</p>
              {loginFields}
            </section>
            <section className="cc-auth-panel cc-auth-panel--accent">
              <p className="cc-auth-eyebrow">{t("New here")}</p>
              <h2 className="cc-auth-title">{t("Create account")}</h2>
              <p className="cc-auth-sub">{t("Please fill in the details required and prepare yourself for an amazing shopping experience!")}</p>
              {registerFields(false)}
            </section>
          </div>
          {messages}
        </div>
        {resetModal}
      </div>
    );
  }

  return (
    <div className={`cc-auth-page${embedded ? " cc-auth-page--embedded" : ""}`}>
      {!embedded ? (
        <div className="cc-auth-bg" aria-hidden="true">
          <span className="cc-auth-orb cc-auth-orb--1" />
          <span className="cc-auth-orb cc-auth-orb--2" />
          <span className="cc-auth-orb cc-auth-orb--3" />
          <span className="cc-auth-shimmer" />
        </div>
      ) : null}
      <div className={`cc-auth-card${code ? " cc-auth-card--wide" : ""}`}>
        {!embedded ? (
          <Link to="/" className="cc-auth-logo"><img src="/images/logo.png" alt="CorporatesClub" /></Link>
        ) : null}
        {code ? (
          <>
            <h1 className="cc-auth-title">{t("Create account")}</h1>
            {firmaNume ? (
              <div className="cc-auth-firma">
                <span>{t("With regards from")}</span>
                <b>{firmaNume}</b>
              </div>
            ) : null}
            <p className="cc-auth-sub">{t("Please fill in the details required and prepare yourself for an amazing shopping experience!")}</p>
            {registerFields(false)}
          </>
        ) : (
          <>
            <div className="cc-auth-tabs">
              <button type="button" className={`cc-auth-tab${tab === "login" ? " active" : ""}`} onClick={() => { setTab("login"); setError(""); }}>{t("Log in")}</button>
              <button type="button" className={`cc-auth-tab${tab === "register" ? " active" : ""}`} onClick={() => { setTab("register"); setError(""); }}>{t("Create account")}</button>
            </div>
            {tab === "login" ? loginFields : registerFields(true)}
          </>
        )}
        {messages}
      </div>
      {resetModal}
    </div>
  );
}
